/**
 * Single source of truth for grouping `claim_context_grouping` row pairs
 * (an atomic claim + the additional combined-span row that `audit-queue.ts`'s
 * `buildClaimRowsWithContextGrouping` deliberately persists per
 * `claim_group_id`) into renderable sections.
 *
 * Frontend (ResultsSummaryHeader, ReportClaimNavigation, analytics) and
 * backend (claims-pdf export) MUST use these helpers — duplicating the rule
 * in either runtime lets the visible-card count and the DB-row count drift
 * again, the exact bug class `pirx-ih09z` and `pirx-vz4vf` fix. Do not
 * re-implement locally.
 *
 * Behaviour is additive group emission: a `claim_context_grouping` cluster
 * emits its combined/context row (`groupPrimary`) AND every atomic constituent
 * (`groupSibling`) as separate, always-visible sections, instead of the
 * combined row replacing/absorbing them. Every visible card counts toward
 * tallies.
 *
 * Contract: input is assumed to be position-sorted (caller responsibility,
 * same as `ClaimsPanel.tsx`'s `sortByPosition(audit.claims)` call before
 * `groupClaimsByContext`). Output preserves that order.
 */

export type ClaimExtractionContext =
  | "atomic"
  | "sentence_context"
  | "neighboring_context";

/**
 * Structural minimum the grouping logic needs. Both the shared
 * `packages/shared/src/types.ts:Claim` (`riskLevel: RiskLevel`, no
 * `extractionContext` / `claimGroupId`) and the looser frontend
 * `frontend/src/types/index.ts:Claim` (`riskLevel: RiskLevel | null`,
 * plus the optional grouping markers) are assignable — `?` on every
 * optional field keeps the structural assignability open in both
 * directions, and `riskLevel: RiskLevel | null` accepts both the strict
 * shared shape and the nullable frontend shape.
 */
export interface GroupableClaim {
  id: string;
  positionStart: number;
  riskLevel?: "RED" | "YELLOW" | "GREEN" | null;
  extractionContext?: ClaimExtractionContext;
  claimGroupId?: string | null;
}

export type ClaimSection<T extends GroupableClaim = GroupableClaim> =
  /** An ordinary atomic claim with no group membership. */
  | { kind: "single"; claim: T }
  /**
   * A `claim_context_grouping`-produced combined/context row (`extractionContext !== "atomic"`)
   * grouping 2+ atomic claims sharing its `claimGroupId`.
   */
  | { kind: "groupPrimary"; claim: T; siblingCount: number }
  /**
   * An atomic constituent belonging to a group; `groupClaimId` references the
   * primary claim's `id`.
   */
  | { kind: "groupSibling"; claim: T; groupClaimId: string }
  /**
   * A merged row whose `claimGroupId` matches no atomic claim in the list
   * (a data inconsistency the backend invariant should prevent, but the
   * view must not silently drop a real assessed claim).
   */
  | { kind: "orphanMerged"; claim: T };

/**
 * Splits a position-sorted claim list into renderable sections so any
 * consumer can render additive `claim_context_grouping` clusters:
 * for each group, `groupPrimary` is emitted at the position of its first
 * atomic member, followed immediately by each atomic member as a
 * `groupSibling` in position order.
 */
export function groupClaimsByContext<T extends GroupableClaim>(
  claims: readonly T[],
): ClaimSection<T>[] {
  const mergedByGroupId = new Map<string, T>();
  for (const claim of claims) {
    const ctx = claim.extractionContext ?? "atomic";
    if (ctx !== "atomic" && claim.claimGroupId) {
      mergedByGroupId.set(claim.claimGroupId, claim);
    }
  }

  const atomicsByGroupId = new Map<string, T[]>();
  for (const claim of claims) {
    if ((claim.extractionContext ?? "atomic") !== "atomic") continue;
    const groupId = claim.claimGroupId;
    if (groupId && mergedByGroupId.has(groupId)) {
      const list = atomicsByGroupId.get(groupId);
      if (list) {
        list.push(claim);
      } else {
        atomicsByGroupId.set(groupId, [claim]);
      }
    }
  }

  const sections: ClaimSection<T>[] = [];
  const emittedGroupIds = new Set<string>();

  for (const claim of claims) {
    if ((claim.extractionContext ?? "atomic") !== "atomic") continue;
    const groupId = claim.claimGroupId ?? null;
    const mergedClaim = groupId ? mergedByGroupId.get(groupId) : undefined;
    if (!groupId || !mergedClaim) {
      sections.push({ kind: "single", claim });
      continue;
    }
    if (emittedGroupIds.has(groupId)) continue;
    emittedGroupIds.add(groupId);

    const siblings = atomicsByGroupId.get(groupId) ?? [];
    sections.push({
      kind: "groupPrimary",
      claim: mergedClaim,
      siblingCount: siblings.length,
    });
    for (const sibling of siblings) {
      sections.push({
        kind: "groupSibling",
        claim: sibling,
        groupClaimId: mergedClaim.id,
      });
    }
  }

  for (const claim of claims) {
    if ((claim.extractionContext ?? "atomic") === "atomic") continue;
    const groupId = claim.claimGroupId ?? null;
    const isOwner = groupId != null &&
      mergedByGroupId.get(groupId) === claim &&
      emittedGroupIds.has(groupId);
    if (!isOwner) {
      sections.push({ kind: "orphanMerged", claim });
    }
  }

  return sections;
}

/**
 * Cross-reference relationship for one claim in a `claim_context_grouping`
 * group, keyed by claim `id` -- no caller-specific claim-number scheme is
 * baked in, so both the frontend's locally-numbered `ClaimSection`
 * (`claimNumber` assigned by its own `groupClaimsByContext` wrapper) and the
 * claims-PDF export's own source-ordered numbering can each resolve these
 * IDs against their own sequence.
 *
 * - `"primary"`: a compound row's pointer to its constituents, identified
 *   by `id`. Never a single target -- a primary can have many siblings --
 *   so this is a neutral, id-list gesture (`siblingIds`) rather than a
 *   link; `siblingIds.length` gives the count callers previously tracked
 *   separately.
 * - `"sibling"`: an atomic constituent's pointer back to its primary,
 *   identified by `primaryId`. `diverges` says whether this sibling's own
 *   verdict (per the caller-supplied `verdictKey`) disagrees with the
 *   primary's.
 */
export type GroupCrossReferenceRef =
  | { kind: "primary"; siblingIds: string[] }
  | { kind: "sibling"; primaryId: string; diverges: boolean };

/**
 * Walks the section list produced by `groupClaimsByContext` and records a
 * cross-reference relationship for EVERY claim that is part of a
 * `claim_context_grouping` group -- not only when a sibling's verdict
 * disagrees with its primary's (see `docs/bugs/report-viewer-claim-
 * context-grouping-unreconciled-risk-verdict.md`: a GREEN sibling sitting
 * right next to its own RED primary, with the two verdicts genuinely and
 * correctly independent, still looked like a freestanding card with no
 * indication it was part of anything).
 *
 * `verdictKey` is caller-supplied rather than baked into `GroupableClaim`
 * (deliberately minimal, see its doc comment above): every known caller
 * shape already carries `riskLevel`/`violationType` natively, so callers
 * pass e.g. `(c) => \`${c.riskLevel ?? "null"}::${c.violationType ?? ""}\``
 * inline.
 *
 * Divergence check uses the grouping relationship already computed by
 * `groupClaimsByContext` (the same source of truth that drives rendering
 * and numbering); it deliberately does NOT consult `positionStart` /
 * `positionEnd` -- those are known-unreliable per
 * docs/bugs/claim-context-grouping-untrusted-position-union.md.
 *
 * Returns a Map keyed by `claim.id`. Claims with no group at all (singles)
 * are absent from the map. Orphan-merged rows have no atomic siblings by
 * construction and never participate.
 */
export function computeGroupCrossReferenceRefs<T extends GroupableClaim>(
  sections: ReadonlyArray<ClaimSection<T>>,
  verdictKey: (claim: T) => string,
): Map<string, GroupCrossReferenceRef> {
  const refs = new Map<string, GroupCrossReferenceRef>();
  // Iterate by index so we always emit the primary BEFORE its siblings in
  // section order (the invariant `groupClaimsByContext` guarantees).
  for (let i = 0; i < sections.length; i++) {
    const primary = sections[i];
    if (primary.kind !== "groupPrimary") continue;
    const siblings: Array<
      Extract<ClaimSection<T>, { kind: "groupSibling" }>
    > = [];
    for (let j = i + 1; j < sections.length; j++) {
      const next = sections[j];
      if (
        next.kind === "groupSibling" && next.groupClaimId === primary.claim.id
      ) {
        siblings.push(next);
      } else if (
        next.kind === "groupPrimary" || next.kind === "orphanMerged" ||
        next.kind === "single"
      ) {
        // A different group or a single starts; the current group's siblings
        // are fully collected.
        break;
      }
    }
    if (siblings.length === 0) continue;
    refs.set(primary.claim.id, {
      kind: "primary",
      siblingIds: siblings.map((sibling) => sibling.claim.id),
    });
    const primaryKey = verdictKey(primary.claim);
    for (const sibling of siblings) {
      refs.set(sibling.claim.id, {
        kind: "sibling",
        primaryId: primary.claim.id,
        diverges: verdictKey(sibling.claim) !== primaryKey,
      });
    }
  }
  return refs;
}

export interface ClaimSectionSummary {
  /** Number of visible claim cards (sections). */
  total: number;
  /**
   * Mirror of the pre-fix `ResultsSummaryHeader.tsx:67`
   * `atRiskCount = claims.filter(c => c.riskLevel !== "GREEN").length`,
   * applied to *sections* not raw rows. `null` / `undefined` `riskLevel`
   * counts toward the ring numerator — consistent with
   * `backend/src/services/audits/rag/audit-rag-finalization.ts:41`'s
   * "any null is YELLOW floor" audit-level semantic. Changing this would
   * silently re-shape the score ring for audits with pending/unscored
   * claims; locked by `packages/shared/src/utils/claim-context-grouping.test.ts`.
   */
  atRisk: number;
  /** Tally of section-level verdicts. `unclassified` sections are NOT counted here. */
  byLevel: { high: number; medium: number; low: number };
  /** Sections whose `riskLevel` is `null` or `undefined`. Subset of `atRisk`. */
  unclassified: number;
}

/**
 * Derived counts every topbar / analytics / PDF consumer needs. Single
 * source of truth — do not recompute these by hand from `audit.claims`,
 * or you reintroduce the inflated-row-count bug this module fixes.
 */
export function summarizeClaimSections<T extends GroupableClaim>(
  claims: readonly T[],
): ClaimSectionSummary {
  const sections = groupClaimsByContext(claims);
  let high = 0;
  let medium = 0;
  let low = 0;
  let unclassified = 0;
  let atRisk = 0;
  for (const section of sections) {
    const lvl = section.claim.riskLevel;
    if (lvl === "RED") {
      high++;
      atRisk++;
    } else if (lvl === "YELLOW") {
      medium++;
      atRisk++;
    } else if (lvl === "GREEN") {
      low++;
    } else {
      // null or undefined: counts as at-risk (matches header L67 + null-is-
      // YELLOW-floor audit-level rule) AND as unclassified for the
      // "Pending review" tile.
      unclassified++;
      atRisk++;
    }
  }
  return {
    total: sections.length,
    atRisk,
    byLevel: { high, medium, low },
    unclassified,
  };
}
